import React, { useEffect, useMemo, useCallback, useContext } from 'react';
import proptypes from 'prop-types';
const { string, number, arrayOf, shape, func } = proptypes;
import {
  params, customType, useLazyDataModel, extend,
  arrayOf as arrayType,
  string as stringType,
  number as numberType,
  bool as boolType,
  shape as shapeType, QueryProvider, hoist
} from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Typography,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell, TableBody
} from '@one-thd/sui-atomic-components';
import { MiniProductListPlaceholder } from './MiniProductListPlaceholder';
import { InfiniteLoader } from '../../../common/InfiniteLoader';
import { ProductItem } from './ProductItem';
import { useProductList } from '../../../hooks/useLists';
import { NOT_FETCHED } from '../../../AddToConstants';

const HoistedInfiniteLoader = hoist(InfiniteLoader, ProductItem);

export const MiniProductList = ({
  listId,
  totalItems,
  onItemSelect,
  onItemSelectAll,
  selectedItems,
  totalItemsSelected
}) => {
  const { channel } = useContext(ExperienceContext);
  const maxPageSize = channel === 'mobile' ? 8 : 16;
  const { storeId, storeZip } = useStore({ varnish: true });
  const [page, setPage] = React.useState(0);
  const PAGE_SIZE = useMemo(() => Math.min(maxPageSize, totalItems), [totalItems]);
  const [listItems, setListItems] = React.useState([]);
  const { data, loading: listLoading } = useProductList({ listId, storeId });

  const items = useMemo(() => {
    const { list } = data || {};
    if (list?.products) {
      return list.products?.results?.map((item) => ({
        itemId: item.itemId,
        quantity: item.quantity
      }));
    }
    return [];
  }, [data]);

  const [
    fetchListProducts, { data: productData, loading: productLoading, variables }
  ] = useLazyDataModel('products', {});

  const loading = useMemo(() => {
    return listLoading || (listItems.length === 0 && items?.length > 0);
  }, [listLoading, listItems.length, productLoading]);

  const selectedItemsMap = useMemo(() => {
    const map = new Map();
    selectedItems.forEach((item) => map.set(item.itemId, item));
    return map;
  }, [selectedItems]);

  const itemsMap = useMemo(() => {
    const map = new Map();
    items.forEach((item) => map.set(item.itemId, item));
    return map;
  }, [items]);

  useEffect(() => {
    if (items.length > 0) {
      const itemsToFetch = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
      if (itemsToFetch.length > 0) {
        fetchListProducts({
          variables: {
            itemIds: itemsToFetch.map((item) => item.itemId),
            storeId,
            zipCode: storeZip
          }
        });
      }
    }
  }, [items, page, PAGE_SIZE, storeId, storeZip, fetchListProducts]);

  useEffect(() => {
    if (productData?.products && variables?.itemIds) {
      const productsMap = new Map();
      productData.products.forEach((product) => productsMap.set(product.itemId, product));

      const itemsToStore = variables.itemIds.map((itemId) => {
        const item = itemsMap.get(itemId);
        const product = productsMap.get(itemId) || item;
        return {
          ...product,
          quantity: item?.quantity || 1
        };
      });

      setListItems((prevItems) => [...prevItems, ...itemsToStore]);
    }
  }, [productData, variables, itemsMap]);

  const loadMoreItem = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const isItemSelected = useCallback((itemId) => {
    return selectedItemsMap.has(itemId);
  }, [selectedItemsMap]);

  const placeholderCount = useMemo(() => Math.min(items.length - listItems.length, PAGE_SIZE),
    [items.length, listItems.length, PAGE_SIZE]
  );

  const isAllSelected = useMemo(() => selectedItems.length === items.length && items.length > 0,
    [selectedItems.length, items.length]
  );

  const handleItemSelect = useCallback((itemId, quantity, basePrice, sku) => {
    onItemSelect({
      listId, itemId, quantity, basePrice, sku
    });
  }, [listId, onItemSelect]);

  const handleSelectAll = useCallback(() => {
    const itemsToAdd = (items || []).map((item) => {
      const product = listItems.find((listItem) => listItem.itemId === item.itemId);
      let basePrice = NOT_FETCHED;
      if (product) {
        basePrice = product?.pricing?.value || 'n/a';
      }
      return {
        ...item,
        basePrice
      };
    });
    onItemSelectAll(listId, itemsToAdd);
  }, [listId, items, listItems, onItemSelectAll]);

  return (
    <div className="sui-w-full" data-testid="mini-product-list">
      {
        loading && <MiniProductListPlaceholder isFull numberOfItems={PAGE_SIZE} />
      }
      {
        listItems.length > 0 && (
          <>
            <div className="sui-mb-4 sui-ml-3 md:sui-ml-6">
              <Typography variant="h4">
                {`${totalItemsSelected} Selected`}
              </Typography>
            </div>
            <Table unlined>
              <TableHead className="sui-w-full">
                <TableRow>
                  <TableCell className="sui-bg-subtle sui-text-left md:sui-px-4 sui-py-2">
                    <Checkbox
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell className="sui-bg-subtle sui-text-left sui-px-1 sui-py-2">Qty</TableCell>
                  <TableCell className="sui-bg-subtle sui-text-left sui-px-2 sui-py-2">Price</TableCell>
                </TableRow>
              </TableHead>
              <QueryProvider
                cacheKey="mini-product-list-products"
                defaultVariables={{
                  storeId,
                  isBrandPricingPolicyCompliant: true
                }}
                dataSource="catalog"
              >
                <HoistedInfiniteLoader
                  wrapperElement={TableBody}
                  loadMoreItem={loadMoreItem}
                  totalItemsCount={items.length}
                  loadedItemsCount={listItems.length}
                >
                  {
                    listItems.map((product, index) => (
                      <ProductItem
                        key={`product-item-${product.itemId}`}
                        itemId={product.itemId}
                        storeId={storeId}
                        quantity={product.quantity}
                        onSelect={handleItemSelect}
                        isSelected={isItemSelected(product.itemId)}
                      />
                    ))
                  }
                </HoistedInfiniteLoader>
              </QueryProvider>
            </Table>
            <MiniProductListPlaceholder isFull={false} numberOfItems={placeholderCount} />
          </>
        )
      }
    </div>
  );
};

MiniProductList.propTypes = {
  listId: string.isRequired,
  totalItems: number.isRequired,
  onItemSelect: func.isRequired,
  onItemSelectAll: func.isRequired,
  selectedItems: arrayOf(shape({
    itemId: string.isRequired,
    quantity: number.isRequired
  })).isRequired,
  totalItemsSelected: number.isRequired
};

MiniProductList.defaultProps = {};

MiniProductList.dataModel = extend({
  list: params({
    id: stringType().isRequired(),
    storeId: stringType(),
    sharedList: boolType()
  }).shape({
    id: stringType(),
    itemCount: numberType(),
    name: stringType(),
    products: params({
      page: numberType(),
      pageSize: numberType(),
      storeId: stringType()
    }).shape({
      results: arrayType(shapeType({
        itemId: stringType(),
        quantity: numberType()
      }))
    })
  }),
  products: params(
    {
      itemIds: arrayType(stringType().isRequired()).isRequired(),
      dataSource: stringType(),
      loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
        svocID: stringType(),
        programTiers: arrayType(
          shapeType({
            tier: stringType(),
            program: stringType()
          })
        )
      })
    }).arrayOf(ProductItem.dataModel.product)
});
